<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import WhatsappConnectionAPI from 'custom/api/whatsappConnection';
import { useLocoI18n } from 'custom/composables/useLocoI18n';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});

const emit = defineEmits(['update']);

const { t } = useLocoI18n();

// The QR code rotates about every 20 seconds, so poll fast while one is shown
const FAST_POLL_MS = 2500;
const SLOW_POLL_MS = 15000;

const status = ref(null);
const isBusy = ref(false);
const pairingPhone = ref('');
const pairingCode = ref('');
let timer = null;

const state = computed(() => status.value?.state || 'connecting');
const isOpen = computed(() => state.value === 'open');
const isWaiting = computed(() => ['qr', 'connecting'].includes(state.value));
const canPair = computed(() =>
  status.value?.capabilities?.includes('pairing_code')
);
const connectedName = computed(() => {
  const me = status.value?.me || {};
  return [me.name, me.phone ? `+${me.phone}` : null]
    .filter(Boolean)
    .join(' · ');
});
const stateColor = computed(() => {
  if (isOpen.value) return 'bg-n-teal-9';
  if (isWaiting.value) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
});

const schedule = () => {
  clearTimeout(timer);
  timer = setTimeout(
    // eslint-disable-next-line no-use-before-define
    refresh,
    isWaiting.value ? FAST_POLL_MS : SLOW_POLL_MS
  );
};

const apply = data => {
  status.value = data;
  if (data.pairing_code) pairingCode.value = data.pairing_code;
  if (data.state === 'open') pairingCode.value = '';
  emit('update', data);
};

const refresh = async () => {
  try {
    const { data } = await WhatsappConnectionAPI.status(props.inboxId);
    apply(data);
  } catch (error) {
    status.value = {
      ...(status.value || {}),
      error: error.response?.data?.error || error.message,
    };
  } finally {
    schedule();
  }
};

const run = async request => {
  isBusy.value = true;
  try {
    const { data } = await request();
    apply({ ...status.value, ...data });
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isBusy.value = false;
    schedule();
  }
};

const reconnect = () => run(() => WhatsappConnectionAPI.connect(props.inboxId));

const requestPairingCode = () => {
  if (!pairingPhone.value.trim()) return;
  run(() =>
    WhatsappConnectionAPI.connect(props.inboxId, pairingPhone.value.trim())
  );
};

const logout = () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('LOCO.WHATSAPP.CONNECTION.LOGOUT_CONFIRM'))) return;
  run(() => WhatsappConnectionAPI.logout(props.inboxId));
};

onMounted(refresh);
onBeforeUnmount(() => clearTimeout(timer));

defineExpose({ refresh });
</script>

<template>
  <div class="flex flex-col gap-5">
    <Banner
      v-if="status && status.gateway_configured === false"
      color="ruby"
      class="w-full"
    >
      {{ t('LOCO.WHATSAPP.CONNECTION.GATEWAY_MISSING') }}
    </Banner>

    <div
      class="flex flex-wrap items-center gap-3 p-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
    >
      <span class="size-2.5 rounded-full flex-shrink-0" :class="stateColor" />
      <div class="flex flex-col flex-1 min-w-0">
        <span class="text-heading-3 text-n-slate-12">
          {{ t(`LOCO.WHATSAPP.CONNECTION.STATE.${state}`) }}
        </span>
        <span v-if="isOpen && connectedName" class="text-sm text-n-slate-11">
          {{
            t('LOCO.WHATSAPP.CONNECTION.CONNECTED_AS', { name: connectedName })
          }}
        </span>
        <span
          v-else-if="status?.reason && !isWaiting"
          class="text-sm text-n-slate-11"
        >
          {{ t(`LOCO.WHATSAPP.CONNECTION.REASON.${status.reason}`) }}
        </span>
        <span
          v-if="isOpen && status?.phone_online === false"
          class="text-sm text-n-amber-11"
        >
          {{ t('LOCO.WHATSAPP.CONNECTION.PHONE_OFFLINE') }}
        </span>
      </div>
      <div class="flex gap-2">
        <Button
          v-if="!isWaiting"
          sm
          faded
          slate
          icon="i-lucide-refresh-cw"
          :label="
            isOpen
              ? t('LOCO.WHATSAPP.CONNECTION.RECONNECT')
              : t('LOCO.WHATSAPP.CONNECTION.CONNECT')
          "
          :is-loading="isBusy"
          @click="reconnect"
        />
        <Button
          v-if="isOpen || isWaiting"
          sm
          faded
          ruby
          icon="i-lucide-unlink"
          :label="t('LOCO.WHATSAPP.CONNECTION.LOGOUT')"
          :disabled="isBusy"
          @click="logout"
        />
      </div>
    </div>

    <p v-if="status?.error" class="text-sm text-n-ruby-11">
      {{ t('LOCO.WHATSAPP.CONNECTION.ERROR', { error: status.error }) }}
    </p>

    <div
      v-if="state === 'qr'"
      class="grid gap-6 md:grid-cols-[auto_1fr] p-5 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
    >
      <div
        class="flex items-center justify-center bg-white rounded-lg size-64 p-2"
      >
        <img
          v-if="status.qr_image"
          :src="status.qr_image"
          :alt="t('LOCO.WHATSAPP.CONNECTION.SCAN_TITLE')"
          class="size-full"
        />
        <Spinner v-else />
      </div>
      <div class="flex flex-col gap-3 min-w-0">
        <h3 class="text-heading-3 text-n-slate-12 flex items-center gap-2">
          <Icon icon="i-woot-whatsapp" class="size-5" />
          {{ t('LOCO.WHATSAPP.CONNECTION.SCAN_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('LOCO.WHATSAPP.CONNECTION.SCAN_STEPS') }}
        </p>

        <div
          v-if="canPair"
          class="flex flex-col gap-2 pt-3 border-t border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LOCO.WHATSAPP.CONNECTION.PAIRING_TITLE') }}
          </span>
          <div v-if="pairingCode" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ t('LOCO.WHATSAPP.CONNECTION.PAIRING_CODE') }}
            </span>
            <code
              class="text-2xl tracking-[0.3em] font-semibold text-n-slate-12"
            >
              {{ pairingCode }}
            </code>
            <span class="text-xs text-n-slate-11">
              {{ t('LOCO.WHATSAPP.CONNECTION.PAIRING_HELP') }}
            </span>
          </div>
          <div v-else class="flex items-end gap-2">
            <Input
              v-model="pairingPhone"
              class="flex-1"
              size="sm"
              :label="t('LOCO.WHATSAPP.CONNECTION.PAIRING_PHONE')"
              placeholder="+5511999999999"
              @enter="requestPairingCode"
            />
            <Button
              sm
              outline
              slate
              :label="t('LOCO.WHATSAPP.CONNECTION.PAIRING_SUBMIT')"
              :is-loading="isBusy"
              @click="requestPairingCode"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
